import { createFileRoute } from '@tanstack/react-router';
import { Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/api-management')({
  component: APIManagementUseCasePage,
  head: () => ({
    meta: [
      { title: 'API Management | Matricks' },
      { name: 'description', content: 'Centralize control of all your APIs with Matricks. Monitor, secure, and manage API traffic with enterprise-grade governance and analytics.' },
    ],
  }),
});

const challenges = [
  "APIs scattered across multiple teams and platforms",
  "Inconsistent security policies and authentication",
  "Limited visibility into API usage and performance",
  "Manual onboarding of new integrations",
  "Difficulty managing API versions and deprecation",
];

const solutions = [
  {
    iconName: "Router",
    title: "Unified Gateway",
    description: "Single entry point for all APIs with intelligent routing and load balancing.",
  },
  {
    iconName: "Shield",
    title: "Consistent Security",
    description: "Apply security policies consistently across all APIs from one place.",
  },
  {
    iconName: "BarChart3",
    title: "Complete Visibility",
    description: "Real-time dashboards showing usage, performance, and errors across all APIs.",
  },
  {
    iconName: "Zap",
    title: "Developer Experience",
    description: "Self-service API discovery and documentation for faster development.",
  },
];

const benefits = [
  "Reduce time to integrate new APIs by 80%",
  "Single dashboard for all API metrics",
  "Consistent security across all endpoints",
  "Automatic rate limiting and throttling",
  "API versioning and lifecycle management",
  "Developer-friendly documentation",
];

const faqItems = [
  {
    question: "How does Matricks differ from other API gateways?",
    answer: "Matricks combines API gateway capabilities with AI agent orchestration, 2000+ pre-built integrations, and fine-grained policy control. It's not just routing - it's a complete integration platform.",
  },
  {
    question: "Can I migrate from my existing API gateway?",
    answer: "Yes, Matricks can run alongside or replace existing gateways. We support gradual migration, allowing you to route traffic incrementally while validating the new setup.",
  },
  {
    question: "How do you handle high traffic volumes?",
    answer: "Matricks is designed for enterprise scale. Our distributed architecture handles millions of requests per second with sub-10ms latency overhead. Auto-scaling ensures performance during traffic spikes.",
  },
];

function APIManagementUseCasePage() {
  return (
    <div>
      <ContentHeader
        title="API Management"
        description="Take control of your API ecosystem. Matricks provides a unified platform to discover, secure, monitor, and manage all your APIs from one place."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Use Cases", href: "/use-cases" },
          { label: "API Management" },
        ]}
        badge="Use Case"
      />

      {/* Challenges */}
      <ContentSection
        id="challenges"
        title="The Challenge"
        description="Modern organizations struggle with API sprawl and management complexity."
      >
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <div className="text-red-500 font-bold">!</div>
              <span>{challenge}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Solution */}
      <ContentSection
        id="solution"
        title="The Matricks Solution"
        background="muted"
      >
        <FeatureShowcase features={solutions} columns={2} variant="cards" />
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Key Benefits"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {benefits.map((benefit) => (
            <div key={benefit} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        id="how-it-works"
        title="How It Works"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              1
            </div>
            <h3 className="mt-4 font-semibold">Connect</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Register your APIs with Matricks or use our 2000+ pre-built connectors.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              2
            </div>
            <h3 className="mt-4 font-semibold">Configure</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Set up security policies, rate limits, and access controls.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              3
            </div>
            <h3 className="mt-4 font-semibold">Monitor</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Track usage, performance, and security with real-time analytics.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Ready to Simplify Your API Management?"
        description="Start managing all your APIs from one platform. Free 14-day trial, no credit card required."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/contact" }}
        background="primary"
      />
    </div>
  );
}
